import AuthService from './authService.js';
import VTOContextStorage from '../utils/VTOContextStorage.js';

/**
 * VTOAuthHandler - Virtual Try-On Authentication Handler
 * Coordinates authentication flows for virtual try-on operations
 */
class VTOAuthHandler {
  constructor() {
    this.authService = new AuthService();
    this.contextStorage = new VTOContextStorage();
    this.isProcessingAuth = false;
    this.authCallbacks = new Map(); // Store callbacks for different operations
  }

  /**
   * Handle authenticated VTO operation
   * @param {string} operation - Operation type (e.g., 'try_on_garment')
   * @param {Object} context - VTO context (person image, canvas items, etc.)
   * @param {Object} operationData - Data specific to the operation
   * @returns {Promise<Object>} Operation result or authentication redirect
   */
  async handleAuthenticatedVTO(operation, context, operationData = {}) {
    try {
      console.log('VTOAuthHandler: Handling authenticated VTO operation:', operation);

      // Check if user is already authenticated
      if (this.authService.isAuthenticated()) {
        console.log('VTOAuthHandler: User already authenticated, proceeding with operation');
        return {
          requiresAuth: false,
          canProceed: true,
          message: 'User authenticated, operation can proceed'
        };
      }

      // User needs authentication - preserve context and redirect
      console.log('VTOAuthHandler: Authentication required, preserving context');

      const preservationResult = await this.preserveVTOContext(context, operation, operationData);

      if (!preservationResult.success) {
        throw new Error('Failed to preserve VTO context before authentication');
      }

      // Redirect to authentication
      await this.redirectToAuthentication(operation, context);

      return {
        requiresAuth: true,
        canProceed: false,
        message: 'Redirecting to authentication',
        contextPreserved: true
      };

    } catch (error) {
      console.error('VTOAuthHandler: Error handling authenticated VTO:', error);
      return {
        requiresAuth: false,
        canProceed: false,
        error: error.message,
        fallbackToManual: true
      };
    }
  }

  /**
   * Preserve VTO context before authentication redirect
   * @param {Object} context - VTO context to preserve
   * @param {string} operation - Operation type
   * @param {Object} operationData - Operation-specific data
   * @returns {Promise<Object>} Preservation result
   */
  async preserveVTOContext(context, operation, operationData = {}) {
    try {
      // Create comprehensive context object
      const vtoContext = {
        ...context,
        operation: {
          type: operation,
          data: operationData,
          timestamp: Date.now(),
          id: this.generateOperationId()
        },
        preservedAt: Date.now(),
        userAgent: navigator.userAgent,
        url: window.location.href
      };

      // Store context with extended TTL for auth flows
      const stored = this.contextStorage.storeContext(vtoContext, 2 * 60 * 60 * 1000); // 2 hours

      if (!stored) {
        throw new Error('Failed to store VTO context');
      }

      console.log('VTOAuthHandler: Context preserved successfully', {
        operation,
        contextKeys: Object.keys(vtoContext),
        operationId: vtoContext.operation.id
      });

      return {
        success: true,
        operationId: vtoContext.operation.id,
        message: 'VTO context preserved successfully'
      };

    } catch (error) {
      console.error('VTOAuthHandler: Context preservation failed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Restore VTO context after authentication return
   * @returns {Promise<Object>} Restoration result with context and operation
   */
  async restoreVTOContext() {
    try {
      console.log('VTOAuthHandler: Attempting to restore VTO context');

      const context = this.contextStorage.retrieveContext();

      if (!context) {
        console.log('VTOAuthHandler: No context found to restore');
        return {
          success: false,
          reason: 'no_context',
          message: 'No VTO context found to restore'
        };
      }

      // Validate context structure
      if (!context.operation || !context.operation.type) {
        console.error('VTOAuthHandler: Invalid context structure');
        this.contextStorage.clearContext();
        return {
          success: false,
          reason: 'invalid_context',
          message: 'Invalid VTO context structure'
        };
      }

      console.log('VTOAuthHandler: Context restored successfully', {
        operation: context.operation.type,
        operationId: context.operation.id,
        preservedAt: new Date(context.preservedAt).toISOString()
      });

      // Clear context after successful restoration
      this.contextStorage.clearContext();

      return {
        success: true,
        context: context,
        operation: context.operation,
        message: 'VTO context restored successfully'
      };

    } catch (error) {
      console.error('VTOAuthHandler: Context restoration failed:', error);
      this.contextStorage.clearContext(); // Clean up on error
      return {
        success: false,
        error: error.message,
        reason: 'restoration_error'
      };
    }
  }

  /**
   * Redirect to SHEYOU authentication with VTO context
   * @param {string} operation - Operation type
   * @param {Object} context - VTO context
   */
  async redirectToAuthentication(operation, context) {
    try {
      // Build VTO-aware return URL
      const returnUrl = this.buildVTOReturnUrl(operation);

      console.log('VTOAuthHandler: Redirecting to authentication', {
        operation,
        returnUrl
      });

      // Use existing AuthService redirect method
      this.authService.redirectToSHEYOULogin(returnUrl);

    } catch (error) {
      console.error('VTOAuthHandler: Authentication redirect failed:', error);
      throw new Error('Failed to redirect to authentication');
    }
  }

  /**
   * Build return URL with VTO restoration parameters
   * @param {string} operation - Operation type
   * @returns {string} Return URL with VTO parameters
   */
  buildVTOReturnUrl(operation) {
    const baseUrl = window.location.origin + window.location.pathname;
    const params = new URLSearchParams(window.location.search);

    // Add VTO restoration parameters
    params.set('vto_restore', 'true');
    params.set('vto_operation', operation);
    params.set('vto_timestamp', Date.now().toString());

    return `${baseUrl}?${params.toString()}`;
  }

  /**
   * Handle authentication callback and context restoration
   * @param {string} token - Authentication token from callback
   * @returns {Promise<Object>} Callback handling result
   */
  async handleAuthCallback(token) {
    try {
      console.log('VTOAuthHandler: Handling authentication callback');

      if (this.isProcessingAuth) {
        console.log('VTOAuthHandler: Already processing authentication');
        return {
          success: false,
          reason: 'already_processing',
          message: 'Authentication already in progress'
        };
      }

      this.isProcessingAuth = true;

      // Handle authentication return using AuthService
      const authResult = await this.authService.handleAuthReturn(token);

      if (!authResult.success) {
        throw new Error(authResult.error || 'Authentication failed');
      }

      console.log('VTOAuthHandler: Authentication successful, attempting context restoration');

      // Attempt to restore VTO context
      const restorationResult = await this.restoreVTOContext();

      return {
        success: true,
        authResult: authResult,
        contextRestoration: restorationResult,
        message: 'Authentication callback handled successfully'
      };

    } catch (error) {
      console.error('VTOAuthHandler: Auth callback handling failed:', error);
      return {
        success: false,
        error: error.message,
        fallbackToManual: true
      };
    } finally {
      this.isProcessingAuth = false;
    }
  }

  /**
   * Check if current URL indicates VTO restoration
   * @returns {Object} VTO restoration status
   */
  checkForVTORestoration() {
    const params = new URLSearchParams(window.location.search);
    const shouldRestore = params.get('vto_restore') === 'true';
    const operation = params.get('vto_operation');
    const timestamp = params.get('vto_timestamp');

    if (shouldRestore) {
      console.log('VTOAuthHandler: VTO restoration detected', {
        operation,
        timestamp: timestamp ? new Date(parseInt(timestamp)).toISOString() : 'unknown'
      });
    }

    return {
      shouldRestore,
      operation,
      timestamp: timestamp ? parseInt(timestamp) : null,
      isValid: shouldRestore && operation && timestamp
    };
  }

  /**
   * Clean up URL parameters after processing
   */
  cleanupUrlParameters() {
    try {
      const url = new URL(window.location);
      const params = url.searchParams;

      // Remove VTO-specific parameters
      params.delete('vto_restore');
      params.delete('vto_operation');
      params.delete('vto_timestamp');

      // Update URL without page reload
      const newUrl = params.toString() ?
        `${url.pathname}?${params.toString()}` :
        url.pathname;

      window.history.replaceState({}, '', newUrl);

      console.log('VTOAuthHandler: URL parameters cleaned up');

    } catch (error) {
      console.error('VTOAuthHandler: URL cleanup failed:', error);
    }
  }

  /**
   * Handle authentication errors with fallback options
   * @param {Error} error - Authentication error
   * @param {Object} context - Current VTO context
   * @returns {Object} Error handling result with fallback options
   */
  handleAuthError(error, context = {}) {
    console.error('VTOAuthHandler: Authentication error:', error);

    const errorResult = {
      success: false,
      error: error.message,
      timestamp: Date.now()
    };

    // Categorize error and provide appropriate fallback
    if (error.message.includes('cancelled') || error.message.includes('denied')) {
      return {
        ...errorResult,
        category: 'user_cancelled',
        fallbackToManual: true,
        message: 'Authentication was cancelled. You can continue using manual positioning.',
        userAction: 'fallback_to_manual'
      };
    }

    if (error.message.includes('network') || error.message.includes('timeout')) {
      return {
        ...errorResult,
        category: 'network_error',
        retryable: true,
        message: 'Network error during authentication. Please try again.',
        userAction: 'retry_auth'
      };
    }

    if (error.message.includes('expired') || error.message.includes('invalid')) {
      return {
        ...errorResult,
        category: 'token_error',
        retryable: true,
        message: 'Authentication session expired. Please try logging in again.',
        userAction: 'retry_auth'
      };
    }

    // Generic error
    return {
      ...errorResult,
      category: 'generic_error',
      fallbackToManual: true,
      retryable: true,
      message: 'Authentication failed. You can try again or continue with manual positioning.',
      userAction: 'show_options'
    };
  }

  /**
   * Check authentication status for VTO operations
   * @returns {Promise<Object>} Authentication status
   */
  async checkAuthenticationStatus() {
    try {
      const authStatus = this.authService.getAuthStatus();

      if (!authStatus.isAuthenticated) {
        return {
          authenticated: false,
          reason: 'no_token',
          message: 'User not authenticated'
        };
      }

      // Check if token is expired
      if (this.authService.isAuthDataExpired()) {
        return {
          authenticated: false,
          reason: 'token_expired',
          message: 'Authentication token expired'
        };
      }

      // Optionally validate token with backend
      try {
        const profile = await this.authService.getUserProfile();
        if (!profile.success) {
          return {
            authenticated: false,
            reason: 'token_invalid',
            message: 'Authentication token is invalid'
          };
        }
      } catch (error) {
        console.warn('VTOAuthHandler: Token validation failed:', error.message);
        // Continue with local validation if backend check fails
      }

      return {
        authenticated: true,
        user: authStatus.user,
        message: 'User authenticated and ready for VTO'
      };

    } catch (error) {
      console.error('VTOAuthHandler: Auth status check failed:', error);
      return {
        authenticated: false,
        reason: 'check_failed',
        error: error.message
      };
    }
  }

  /**
   * Register callback for specific VTO operation
   * @param {string} operationId - Operation ID
   * @param {Function} callback - Callback function to execute after auth
   */
  registerOperationCallback(operationId, callback) {
    this.authCallbacks.set(operationId, {
      callback,
      registeredAt: Date.now()
    });

    // Clean up old callbacks (older than 1 hour)
    const oneHour = 60 * 60 * 1000;
    for (const [id, data] of this.authCallbacks.entries()) {
      if (Date.now() - data.registeredAt > oneHour) {
        this.authCallbacks.delete(id);
      }
    }
  }

  /**
   * Execute registered callback for operation
   * @param {string} operationId - Operation ID
   * @param {Object} context - Restored context
   * @returns {Promise<any>} Callback result
   */
  async executeOperationCallback(operationId, context) {
    const callbackData = this.authCallbacks.get(operationId);

    if (!callbackData) {
      console.log('VTOAuthHandler: No callback registered for operation:', operationId);
      return null;
    }

    try {
      const result = await callbackData.callback(context);
      this.authCallbacks.delete(operationId); // Clean up after execution
      return result;
    } catch (error) {
      console.error('VTOAuthHandler: Callback execution failed:', error);
      this.authCallbacks.delete(operationId);
      throw error;
    }
  }

  /**
   * Generate unique operation ID
   * @returns {string} Unique operation ID
   */
  generateOperationId() {
    return `vto_op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get context storage metadata
   * @returns {Object|null} Context metadata
   */
  getContextMetadata() {
    return this.contextStorage.getContextMetadata();
  }

  /**
   * Clear any stored VTO context
   */
  clearStoredContext() {
    this.contextStorage.clearContext();
    console.log('VTOAuthHandler: Stored context cleared');
  }

  /**
   * Check if VTO context exists
   * @returns {boolean} True if valid context exists
   */
  hasStoredContext() {
    return this.contextStorage.hasValidContext();
  }
}

export default VTOAuthHandler;